import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FLEXXI_KYC_STATUS_LABELS, type FlexxiKycProfileStatus } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import { ListPageShell } from "../components/shell/ListPageShell";
import { DataTable, type Column } from "../components/DataTable";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { Badge, Button, Textarea, useConfirm } from "../components/ui";

interface KycRow {
  userId: string;
  email: string;
  displayName: string | null;
  kycStatus: FlexxiKycProfileStatus;
  kycVerifiedAt: string | null;
  legalName: string | null;
  submittedAt: string | null;
}

type KycListResponse = {
  items: KycRow[];
  total: number;
  page: number;
  pageSize: number;
};

type KycDetailResponse = {
  user: { id: string; email: string; displayName: string | null };
  profile: { kycStatus: FlexxiKycProfileStatus; kycVerifiedAt: string | null };
  submission: {
    legalName: string;
    idType: string;
    idNumberMasked: string;
    status: string;
    reviewNote: string | null;
    submittedAt: string | null;
    correspondence: {
      line1: string;
      line2: string | null;
      city: string;
      postcode: string;
      state: string;
      country: string | null;
    };
    docs: { idFrontUrl: string | null; idBackUrl: string | null; selfieUrl: string | null };
  } | null;
};

const VIEW_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "VERIFIED", label: "Verified" },
  { value: "REJECTED", label: "Rejected" },
  { value: "UPDATE_REQUIRED", label: "Update required" },
  { value: "ALL", label: "All" },
];

function statusColor(status: FlexxiKycProfileStatus): "green" | "amber" | "pink" | "gray" | "blue" {
  if (status === "VERIFIED") return "green";
  if (status === "PENDING") return "amber";
  if (status === "REJECTED" || status === "UPDATE_REQUIRED") return "pink";
  return "gray";
}

function DocLink({ href, label }: { href: string | null; label: string }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="block rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs text-brand-blue hover:bg-hover"
    >
      {label}
    </a>
  );
}

export function FlexxiKycVerificationPanel() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [view, setView] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [updateNote, setUpdateNote] = useState("");

  const listQ = useQuery({
    queryKey: ["flexxi-kyc-admin", view, search],
    queryFn: () => {
      const params = new URLSearchParams({ page: "1", pageSize: "50" });
      if (view !== "ALL") params.set("status", view);
      if (search.trim()) params.set("q", search.trim());
      return api<KycListResponse>(`/admin/flexxi/kyc?${params}`);
    },
  });

  const detailQ = useQuery({
    queryKey: ["flexxi-kyc-admin-detail", selectedId],
    queryFn: () => api<KycDetailResponse>(`/admin/flexxi/kyc/${selectedId}`),
    enabled: !!selectedId,
  });

  const reviewMut = useMutation({
    mutationFn: (args: { userId: string; approve: boolean; reviewNote?: string }) =>
      api(`/admin/flexxi/kyc/${args.userId}/review`, {
        method: "POST",
        body: { approve: args.approve, reviewNote: args.reviewNote },
      }),
    onSuccess: () => {
      toast("success", "Review saved");
      setReviewNote("");
      void qc.invalidateQueries({ queryKey: ["flexxi-kyc-admin"] });
      void qc.invalidateQueries({ queryKey: ["flexxi-kyc-admin-detail", selectedId] });
    },
    onError: (e: Error) => toast("error", e instanceof ApiError ? e.message : "Review failed"),
  });

  const requestUpdateMut = useMutation({
    mutationFn: (args: { userId: string; note?: string }) =>
      api(`/admin/flexxi/kyc/${args.userId}/request-update`, {
        method: "POST",
        body: { note: args.note },
      }),
    onSuccess: () => {
      toast("success", "Update requested");
      setUpdateNote("");
      void qc.invalidateQueries({ queryKey: ["flexxi-kyc-admin"] });
      void qc.invalidateQueries({ queryKey: ["flexxi-kyc-admin-detail", selectedId] });
    },
    onError: (e: Error) => toast("error", e instanceof ApiError ? e.message : "Request failed"),
  });

  const rows = listQ.data?.items ?? [];
  const detail = detailQ.data;

  const columns: Column<KycRow>[] = [
    {
      key: "customer",
      header: "Customer",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{r.displayName ?? r.legalName ?? "—"}</p>
          <p className="truncate text-[11px] text-muted">{r.email}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge color={statusColor(r.kycStatus)}>{FLEXXI_KYC_STATUS_LABELS[r.kycStatus]}</Badge>,
    },
    {
      key: "submitted",
      header: "Submitted",
      hideSm: true,
      render: (r) => (
        <span className="text-xs text-muted">{r.submittedAt ? fmtDate(r.submittedAt) : "—"}</span>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.userId}
        loading={listQ.isLoading}
        entityLabel="Customer"
        fillViewport
        activeKey={selectedId ?? undefined}
        onRowClick={(r) => setSelectedId(r.userId)}
        toolbar={{
          search: {
            value: searchDraft,
            onChange: setSearchDraft,
            onSubmit: () => setSearch(searchDraft),
            placeholder: "Search email or name…",
          },
          views: {
            options: VIEW_OPTIONS,
            value: view,
            onChange: setView,
          },
          onRefresh: () => void listQ.refetch(),
        }}
        empty={{
          title: "No verifications",
          hint: view === "PENDING" ? "Pending customer submissions will appear here." : "Try another filter.",
        }}
      />

      <RightSidebarPanel
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={detail?.user.displayName ?? detail?.user.email ?? "Verification"}
        footer={
          detail?.profile.kycStatus === "PENDING" && detail.submission ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                disabled={reviewMut.isPending}
                onClick={() => {
                  if (!selectedId) return;
                  if (!reviewNote.trim()) {
                    toast("error", "Add a review note when rejecting");
                    return;
                  }
                  confirm({
                    title: "Reject verification?",
                    message: "The customer will be asked to submit again.",
                    confirmLabel: "Reject",
                    danger: true,
                    action: () => reviewMut.mutate({ userId: selectedId, approve: false, reviewNote: reviewNote.trim() }),
                  });
                }}
              >
                Reject
              </Button>
              <Button
                disabled={reviewMut.isPending}
                onClick={() => selectedId && reviewMut.mutate({ userId: selectedId, approve: true })}
              >
                Approve
              </Button>
            </div>
          ) : detail?.profile.kycStatus === "VERIFIED" ? (
            <div className="flex justify-end">
              <Button
                variant="outline"
                disabled={requestUpdateMut.isPending}
                onClick={() => {
                  if (!selectedId) return;
                  requestUpdateMut.mutate({ userId: selectedId, note: updateNote.trim() || undefined });
                }}
              >
                Request update
              </Button>
            </div>
          ) : null
        }
      >
        {detail && (
          <div className="space-y-5 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Account</p>
              <p>{detail.user.email}</p>
              <Badge color={statusColor(detail.profile.kycStatus)}>
                {FLEXXI_KYC_STATUS_LABELS[detail.profile.kycStatus]}
              </Badge>
            </div>

            {detail.submission ? (
              <>
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">Identity</p>
                  <p>{detail.submission.legalName}</p>
                  <p className="text-muted">
                    {detail.submission.idType} · {detail.submission.idNumberMasked}
                  </p>
                </div>
                <div className="grid gap-2">
                  <DocLink href={detail.submission.docs.idFrontUrl} label="MyKad front" />
                  <DocLink href={detail.submission.docs.idBackUrl} label="MyKad back" />
                  <DocLink href={detail.submission.docs.selfieUrl} label="Selfie" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">Address</p>
                  <p className="text-muted">
                    {detail.submission.correspondence.line1}
                    {detail.submission.correspondence.line2
                      ? `, ${detail.submission.correspondence.line2}`
                      : ""}
                    <br />
                    {detail.submission.correspondence.postcode} {detail.submission.correspondence.city},{" "}
                    {detail.submission.correspondence.state}
                  </p>
                </div>
                {detail.profile.kycStatus === "PENDING" && (
                  <Textarea
                    label="Review note"
                    hint="Required when rejecting"
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                  />
                )}
                {detail.profile.kycStatus === "VERIFIED" && (
                  <Textarea
                    label="Message to customer (optional)"
                    value={updateNote}
                    onChange={(e) => setUpdateNote(e.target.value)}
                  />
                )}
              </>
            ) : (
              <p className="text-muted">No submission on file.</p>
            )}
          </div>
        )}
      </RightSidebarPanel>
      {dialog}
    </>
  );
}

/** @deprecated Use Commerce hub at /commerce/verifications?tab=flexxi */
export function FlexxiVerifications() {
  return (
    <ListPageShell>
      <FlexxiKycVerificationPanel />
    </ListPageShell>
  );
}
